import { PayloadAction, createSlice } from '@reduxjs/toolkit'

const STORAGE_KEY = 'helper_text_state'

interface HelperTextState {
  dismissedSteps: Record<string, boolean>
  autoDismissedSteps: Record<string, boolean>
}

const loadPersistedState = (): HelperTextState => {
  if (typeof window === 'undefined') return { dismissedSteps: {}, autoDismissedSteps: {} }

  try {
    const persistedData = localStorage.getItem(STORAGE_KEY)
    if (persistedData) {
      const { state, timestamp } = JSON.parse(persistedData)
      // Check if the state is less than 30 days old
      if (timestamp && new Date().getTime() - timestamp < 30 * 24 * 60 * 60 * 1000) {
        return {
          ...state,
          autoDismissedSteps: state.autoDismissedSteps || {}
        }
      }
    }
  } catch (err) {
    console.error('Error loading persisted helper text state:', err)
  }

  return { dismissedSteps: {}, autoDismissedSteps: {} }
}

const saveState = (state: HelperTextState) => {
  if (typeof window === 'undefined') return

  try {
    const persistedData = {
      state,
      timestamp: new Date().getTime()
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(persistedData))
  } catch (err) {
    console.error('Error saving helper text state:', err)
  }
}

const initialState: HelperTextState = loadPersistedState()

const helperTextSlice = createSlice({
  name: 'helperText',
  initialState,
  reducers: {
    dismissHelperText: (state, action: PayloadAction<string>) => {
      state.dismissedSteps[action.payload] = true
      saveState(state)
    },
    autoDismissHelperText: (state, action: PayloadAction<string>) => {
      state.autoDismissedSteps[action.payload] = true
      saveState(state)
    },
    resetHelperText: (state, action: PayloadAction<string>) => {
      delete state.dismissedSteps[action.payload]
      delete state.autoDismissedSteps[action.payload]
      saveState(state)
    },
    resetAllHelperText: (state) => {
      state.dismissedSteps = {}
      state.autoDismissedSteps = {}
      saveState(state)
    }
  }
})

export const { dismissHelperText, autoDismissHelperText, resetHelperText, resetAllHelperText } = helperTextSlice.actions
export default helperTextSlice.reducer
